import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const AdminSponsors = () => {
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialog, setDialog] = useState({ isOpen: false, type: 'alert', title: '', message: '', onConfirm: null });
  const navigate = useNavigate();

  const showAlert = (title, message, onConfirm = null) => {
    setDialog({ isOpen: true, type: 'alert', title, message, onConfirm });
  };

  const showConfirm = (title, message, onConfirm) => {
    setDialog({ isOpen: true, type: 'confirm', title, message, onConfirm });
  };

  const closeDialog = () => {
    setDialog(prev => ({ ...prev, isOpen: false }));
  };

  useEffect(() => {
    fetchSponsors();
  }, [currentPage, searchTerm]);

  const fetchSponsors = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      
      if (!token) {
        showAlert('Authentication Required', 'Please log in again.', () => navigate('/admin/login'));
        return;
      }

      const params = new URLSearchParams({
        page: currentPage.toString(),
        size: '10'
      });

      if (searchTerm) params.append('search', searchTerm);

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sponsors/?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 401) {
        localStorage.removeItem('adminToken');
        showAlert('Session Expired', 'Your session has expired. Please log in again.', () => navigate('/admin/login'));
        return;
      }

      if (!response.ok) throw new Error('Failed to fetch sponsors');

      const data = await response.json();
      setSponsors(data.items);
      setTotalPages(data.pages);
    } catch (error) {
      console.error('Error fetching sponsors:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (sponsor) => {
    navigate(`/admin/sponsors/edit/${sponsor.id}`);
  };

  const executeDelete = async (sponsorId) => {
    try {
      const token = localStorage.getItem('adminToken');
      
      if (!token) {
        showAlert('Authentication Required', 'Please log in again.', () => navigate('/admin/login'));
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sponsors/${sponsorId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 401) {
        localStorage.removeItem('adminToken');
        showAlert('Session Expired', 'Your session has expired. Please log in again.', () => navigate('/admin/login'));
        return;
      }

      if (!response.ok) throw new Error('Failed to delete sponsor');

      fetchSponsors();
      showAlert('Deleted', 'Sponsor has been successfully deleted.');
    } catch (error) {
      console.error('Error deleting sponsor:', error);
      showAlert('Error', 'Failed to delete sponsor');
    }
  };

  const handleDelete = (sponsorId) => {
    showConfirm(
      'Delete Sponsor',
      'Are you sure you want to completely delete this sponsor? This action cannot be undone.',
      () => executeDelete(sponsorId)
    );
  };

  const handleToggleActive = async (sponsorId) => {
    try {
      const token = localStorage.getItem('adminToken');
      
      if (!token) {
        showAlert('Authentication Required', 'Please log in again.', () => navigate('/admin/login'));
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sponsors/${sponsorId}/toggle-active`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 401) {
        alert('Your session has expired. Please log in again.');
        localStorage.removeItem('adminToken');
        navigate('/admin/login');
        return;
      }

      if (!response.ok) throw new Error('Failed to toggle sponsor status');

      fetchSponsors();
    } catch (error) {
      console.error('Error toggling sponsor status:', error);
      showAlert('Error', 'Failed to toggle sponsor status');
    }
  };



  // Toggle Switch Component
  const ToggleSwitch = ({ isActive, onToggle, sponsorName }) => (
    <div className="flex items-center gap-3">
      <button
        onClick={onToggle}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-electric-yellow/50 ${
          isActive ? 'bg-green-500' : 'bg-gray-600'
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-300 ${
            isActive ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
      <span className={`text-sm font-medium ${isActive ? 'text-green-400' : 'text-gray-400'}`}>
        {isActive ? 'Active' : 'Inactive'}
      </span>
    </div>
  );

  const SponsorCard = ({ sponsor }) => (
    <div className="bg-charcoal/40 backdrop-blur-sm border border-slate-dark/30 rounded-2xl p-4 md:p-6 hover:scale-[1.02] transition-transform duration-300">
      <div className="flex flex-col sm:flex-row items-start gap-4">
        <img
          src={sponsor.logo}
          alt={sponsor.name}
          className="w-20 h-20 sm:w-16 sm:h-16 object-contain bg-transparent border border-slate-dark/30 rounded-lg p-2"
        />
        <div className="flex-1 text-left w-full">
          <div className="mb-2">
            <h3 className="font-bold text-warm-white text-xl text-left">{sponsor.name}</h3>
          </div>
          <p className="text-electric-yellow text-sm mb-2 text-left">{sponsor.category}</p>
          <p className="text-soft-gray text-sm mb-3 text-left">{sponsor.description.substring(0, 100)}...</p>
          <div className="flex items-start gap-4 text-sm text-soft-gray mb-4 flex-col sm:flex-row">
            <span className="text-left">🌐 {sponsor.website}</span>
            <span className="text-left">🤝 {(sponsor.why_partnered || sponsor.whyPartnered || '').substring(0, 30)}...</span>
          </div>
          {sponsor.testimonial.name && (
            <div className="bg-midnight/60 rounded-lg p-3 mb-4">
              <p className="text-electric-yellow text-sm italic text-left">"{sponsor.testimonial.content}"</p>
              <p className="text-soft-gray text-xs mt-1 text-left">
                - {sponsor.testimonial.name}, {sponsor.testimonial.role}
              </p>
            </div>
          )}
        </div>
      </div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-4 border-t border-slate-dark/30 mt-4">
        {/* Toggle Switch on the left */}
        <ToggleSwitch 
          isActive={sponsor.is_active ?? sponsor.isActive}
          onToggle={() => handleToggleActive(sponsor.id)}
          sponsorName={sponsor.name}
        />
        
        {/* Edit and Delete buttons on the right */}
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <button
            onClick={() => handleEdit(sponsor)}
            className="flex-1 sm:flex-none bg-electric-yellow hover:bg-electric-yellow/90 text-midnight px-4 py-2 rounded-lg transition-colors duration-300 text-center font-bold"
          >
            Edit
          </button>
          <button
            onClick={() => handleDelete(sponsor.id)}
            className="flex-1 sm:flex-none bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg transition-colors duration-300 text-center"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-electric-yellow text-lg">Loading sponsors...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <h2 className="font-clash text-2xl md:text-3xl font-bold text-electric-yellow">Sponsors</h2>
        <button
          onClick={() => navigate('/admin/sponsors/create')}
          className="w-full sm:w-auto bg-electric-yellow text-midnight px-6 py-3 rounded-xl font-bold hover:bg-electric-yellow/90 transition-colors duration-300 text-center"
        >
          + Add Sponsor
        </button>
      </div>



      {/* Search */}
      <div className="bg-charcoal/40 backdrop-blur-sm border border-slate-dark/30 rounded-2xl p-6">
        <input
          type="text"
          placeholder="Search sponsors..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full bg-midnight/80 border border-slate-dark rounded-xl px-4 py-3 text-warm-white placeholder-soft-gray focus:outline-none focus:ring-2 focus:ring-electric-yellow/50"
        />
      </div>

      {/* Sponsors List */}
      <div className="space-y-4">
        {sponsors.length === 0 ? (
          <div className="text-center py-12 text-soft-gray">
            No sponsors found. Create your first sponsor!
          </div>
        ) : (
          sponsors.map((sponsor) => (
            <SponsorCard key={sponsor.id} sponsor={sponsor} />
          ))
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-4 pt-6">
          <button
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            className="bg-charcoal/40 text-warm-white px-4 py-2 rounded-lg disabled:opacity-50 hover:bg-electric-yellow/10 transition-colors duration-300"
          >
            Previous
          </button>
          <span className="text-soft-gray">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
            className="bg-charcoal/40 text-warm-white px-4 py-2 rounded-lg disabled:opacity-50 hover:bg-electric-yellow/10 transition-colors duration-300"
          >
            Next
          </button>
        </div>
      )}

      {/* Custom Dialog Modal */}
      {dialog.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-midnight/80 backdrop-blur-sm">
          <div className="bg-slate-dark border border-electric-yellow/30 rounded-2xl p-6 max-w-md w-full shadow-2xl animate-in fade-in zoom-in duration-200">
            <h3 className="text-xl font-bold text-electric-yellow mb-2">{dialog.title}</h3>
            <p className="text-soft-gray mb-6">{dialog.message}</p>
            <div className="flex justify-center gap-3">
              {dialog.type === 'confirm' && (
                <button
                  onClick={closeDialog}
                  className="px-4 py-2 rounded-lg font-medium text-soft-gray hover:text-white hover:bg-white/5 transition-colors"
                >
                  Cancel
                </button>
              )}
              <button
                onClick={() => {
                  if (dialog.onConfirm) dialog.onConfirm();
                  closeDialog();
                }}
                className={`px-4 py-2 rounded-lg font-bold transition-transform hover:scale-105 ${
                  dialog.type === 'confirm' 
                    ? 'bg-red-500 text-white shadow-lg shadow-red-500/20' 
                    : 'bg-electric-yellow text-midnight shadow-lg shadow-electric-yellow/20'
                }`}
              >
                {dialog.type === 'confirm' ? 'Delete' : 'OK'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSponsors;